import AgoraRTC from 'agora-rtc-sdk-ng';
import { ref, onBeforeUnmount } from 'vue';
import { AGORA_CLIENT_CONFIG, CALL_RECOVERY_RETRY_MS, QR_CALL_ERROR } from '../../constants/qr';

export function useAgoraCall() {
    const micOn = ref(true);
    const micAllowed = ref(true);
    const camOn = ref(false);
    const audioOnly = ref(false);
    const reconnecting = ref(false);
    const remoteVideoOn = ref(false);
    const autoplayBlocked = ref(false);

    // SDK objects must stay outside Vue reactivity; Proxies break them
    let client = null;
    let mic = null;
    let cam = null;
    let remoteUser = null;
    let facing = 'user';
    let channelName = null;
    let joinedUid = null;
    let remoteEl = null;
    let handlers = {};
    let generation = 0;
    let peerUid = null;
    let savedCredentials = null;
    let savedElements = null;
    let toggleQueue = Promise.resolve();
    let removePlaybackListeners = null;
    let playbackFailed = false;
    let audioInterrupted = false;
    let videoAllowed = true;
    const publications = new WeakMap();

    function registerPlaybackEvents(current) {
        removePlaybackListeners?.();
        const onAutoplayFailed = () => {
            if (current !== generation) return;
            playbackFailed = true;
            autoplayBlocked.value = true;
        };
        const onAudioContextChanged = state => {
            if (current !== generation) return;
            if (state === 'interrupted' || state === 'suspended') audioInterrupted = true;
            else if (state === 'running') audioInterrupted = false;
            autoplayBlocked.value = audioInterrupted || playbackFailed;
        };
        AgoraRTC.on('autoplay-failed', onAutoplayFailed);
        AgoraRTC.on('audio-context-state-changed', onAudioContextChanged);
        removePlaybackListeners = () => {
            AgoraRTC.off('autoplay-failed', onAutoplayFailed);
            AgoraRTC.off('audio-context-state-changed', onAudioContextChanged);
        };
    }

    function detachTransport(transport) {
        transport.removeAllListeners();
        const publication = publications.get(transport);
        if (!publication) return;
        publication.ready = false;
        publication.waits.forEach((resolve, timer) => {
            clearTimeout(timer);
            resolve(false);
        });
        publication.waits.clear();
    }

    function waitForRenewal(transport) {
        return new Promise(resolve => {
            const waits = publications.get(transport).waits;
            const timer = setTimeout(() => {
                waits.delete(timer);
                resolve(true);
            }, CALL_RECOVERY_RETRY_MS);
            waits.set(timer, resolve);
        });
    }

    function ensurePublished(transport, tracks) {
        const current = generation;
        const publication = publications.get(transport);
        if (!publication?.ready) return Promise.resolve();
        publication.queue = publication.queue.then(async () => {
            if (current !== generation || client !== transport) return;
            const pending = tracks.filter(track => track && !publication.tracks.has(track) &&
                ((track === mic && micOn.value) || (track === cam && camOn.value)));
            if (!pending.length) return;
            await transport.publish(pending);
            pending.forEach(track => publication.tracks.add(track));
        });
        return publication.queue;
    }

    async function prepareTracks() {
        const current = ++generation;
        if (!micAllowed.value) micOn.value = true;
        micAllowed.value = true;
        videoAllowed = true;
        registerPlaybackEvents(current);
        try {
            const track = await AgoraRTC.createMicrophoneAudioTrack();
            if (current !== generation) {
                track.close();
                return;
            }
            mic = track;
            if (!micOn.value) await track.setMuted(true);
        } catch (error) {
            if (current !== generation) return;
            throw Object.assign(new Error('Microphone unavailable'), { code: QR_CALL_ERROR.MIC_REQUIRED, cause: error });
        }

        if (current !== generation) return;
        try {
            const track = await AgoraRTC.createCameraVideoTrack({ facingMode: facing });
            if (current !== generation) {
                track.close();
                return;
            }
            cam = track;
            camOn.value = true;
            audioOnly.value = false;
        } catch {
            if (current !== generation) return;
            cam = null;
            camOn.value = false;
            audioOnly.value = true;
        }
    }

    function configureMedia({ isAudio = true, isVideo = true } = {}) {
        micAllowed.value = isAudio;
        videoAllowed = isVideo;
        if (!isAudio) {
            mic?.close();
            mic = null;
            micOn.value = false;
        }
        if (!isVideo) {
            cam?.close();
            cam = null;
            camOn.value = false;
            audioOnly.value = isAudio;
        }
    }

    function registerEvents(c) {
        const current = generation;
        const callbacks = handlers;
        const target = remoteEl;
        const isCurrent = () => current === generation && client === c;
        const versions = new Map();
        const mediaReconnects = new Set();
        let renewal = null;
        const isPeer = user => {
            if (user.uid === joinedUid || !isCurrent()) return false;
            if (peerUid === null) peerUid = user.uid;
            return peerUid === user.uid;
        };

        c.on('user-joined', user => {
            if (isPeer(user)) callbacks.onRemoteJoined?.();
        });

        c.on('user-published', async (user, mediaType) => {
            if (!['audio', 'video'].includes(mediaType) || !isPeer(user)) return;
            if ((mediaType === 'audio' && !micAllowed.value) || (mediaType === 'video' && !videoAllowed)) return;
            const version = (versions.get(mediaType) || 0) + 1;
            versions.set(mediaType, version);
            try {
                await c.subscribe(user, mediaType);
                if (!isCurrent() || versions.get(mediaType) !== version) return;
                remoteUser = user;
                callbacks.onRemoteJoined?.();
                if (!isCurrent()) return;
                if (mediaType === 'video') {
                    user.videoTrack.play(target);
                    remoteVideoOn.value = true;
                } else {
                    user.audioTrack.play();
                }
            } catch (error) {
                if (!isCurrent() || versions.get(mediaType) !== version || error.code === 'TRACK_IS_NOT_PUBLISHED') return;
                if (mediaType === 'video') remoteVideoOn.value = false;
                else callbacks.onError?.(QR_CALL_ERROR.MEDIA_FAILED);
            }
        });

        c.on('user-unpublished', (user, mediaType) => {
            if (!isPeer(user)) return;
            versions.set(mediaType, (versions.get(mediaType) || 0) + 1);
            if (mediaType === 'video') remoteVideoOn.value = false;
        });

        c.on('user-left', (user, reason) => {
            if (!isPeer(user)) return;
            ['audio', 'video'].forEach(type => versions.set(type, (versions.get(type) || 0) + 1));
            remoteUser = null;
            remoteVideoOn.value = false;
            callbacks.onRemoteLeft?.(reason);
        });

        c.on('connection-state-change', (state, previous, reason) => {
            if (!isCurrent()) return;
            if (state === 'DISCONNECTED' && !publications.get(c).ready) return;
            reconnecting.value = state === 'RECONNECTING';
            return callbacks.onConnectionStateChange?.(state, reason, { channel: channelName, uid: joinedUid });
        });

        c.on('media-reconnect-start', uid => {
            if (!isCurrent() || (uid !== joinedUid && uid !== peerUid)) return;
            mediaReconnects.add(uid);
            callbacks.onMediaReconnecting?.(true);
        });
        c.on('media-reconnect-end', uid => {
            if (!isCurrent()) return;
            mediaReconnects.delete(uid);
            callbacks.onMediaReconnecting?.(mediaReconnects.size > 0);
        });

        c.on('token-privilege-will-expire', () => {
            if (!isCurrent()) return;
            if (renewal) return renewal;
            renewal = (async () => {
                try {
                    for (let index = 0; index < 2; index++) {
                        try {
                            const token = await callbacks.onTokenWillExpire?.({ channel: channelName, uid: joinedUid });
                            if (token && isCurrent()) await c.renewToken(token);
                            return;
                        } catch (error) {
                            if (!isCurrent()) return;
                            const retry = callbacks.onRenewalFailed?.(error) !== false;
                            if (!retry || index === 1 || !isCurrent() || !await waitForRenewal(c)) return;
                        }
                    }
                } finally {
                    renewal = null;
                }
            })();
            return renewal;
        });
        c.on('token-privilege-did-expire', () => {
            if (isCurrent()) return callbacks.onTokenExpired?.({ channel: channelName, uid: joinedUid });
        });
    }

    async function join({ appId, channel, token, uid, isAudio = true, isVideo = true }, elements, eventHandlers = {}) {
        configureMedia({ isAudio, isVideo });
        const current = generation;
        const localMic = mic;
        const localCam = cam;
        savedCredentials = { appId, channel, token, uid, isAudio, isVideo };
        savedElements = elements;
        remoteEl = elements.remoteEl;
        handlers = eventHandlers;
        channelName = channel;

        const transport = AgoraRTC.createClient(AGORA_CLIENT_CONFIG);
        client = transport;
        publications.set(transport, { tracks: new Set(), queue: Promise.resolve(), ready: false, waits: new Map() });
        registerEvents(transport);
        const actualUid = await transport.join(appId, channel, token || null, uid ?? null);
        if (current !== generation || client !== transport) {
            await transport.leave();
            return;
        }
        joinedUid = actualUid;

        await toggleQueue;
        if (current !== generation || client !== transport) return;
        publications.get(transport).ready = true;
        await ensurePublished(transport, [localMic, localCam]);
        if (current !== generation || client !== transport) return;
        if (camOn.value) localCam?.play(elements.localEl);
    }

    async function rejoin(token) {
        const current = generation;
        const credentials = { ...savedCredentials, token, uid: joinedUid };
        const elements = savedElements;
        const callbacks = handlers;
        const previous = client;
        client = null;
        remoteUser = null;
        reconnecting.value = false;
        remoteVideoOn.value = false;
        if (previous) {
            detachTransport(previous);
            await previous.leave();
        }
        if (current !== generation) return;
        await join(credentials, elements, callbacks);
    }

    function enqueueToggle(operation) {
        const current = generation;
        toggleQueue = toggleQueue.then(async () => {
            if (current !== generation) return;
            try {
                await operation(() => current === generation);
            } catch {
                if (current === generation) handlers.onError?.(QR_CALL_ERROR.MEDIA_FAILED);
            }
        });
        return toggleQueue;
    }

    function toggleMic() {
        return enqueueToggle(async isCurrent => {
            const track = mic;
            if (!track || !micAllowed.value) return;
            const next = !micOn.value;
            await track.setMuted(!next);
            if (!isCurrent() || !micAllowed.value) return;
            micOn.value = next;
            if (next && client) await ensurePublished(client, [track]);
        });
    }

    function toggleCam() {
        return enqueueToggle(async isCurrent => {
            const track = cam;
            if (!track || !videoAllowed) return;
            const next = !camOn.value;
            await track.setEnabled(next);
            if (!isCurrent() || !videoAllowed) return;
            camOn.value = next;
            if (next && client) await ensurePublished(client, [track]);
            if (!isCurrent()) return;
            if (next && savedElements) track.play(savedElements.localEl);
        });
    }

    async function switchCamera() {
        const current = generation;
        const track = cam;
        if (!track) return;
        const next = facing === 'user' ? 'environment' : 'user';
        await track.setDevice({ facingMode: next });
        if (current === generation) facing = next;
    }

    function resumeMedia() {
        playbackFailed = false;
        autoplayBlocked.value = audioInterrupted;
        try {
            AgoraRTC.resumeAudioContext();
            remoteUser?.audioTrack?.play();
            if (remoteVideoOn.value) remoteUser?.videoTrack?.play(savedElements.remoteEl);
            if (camOn.value && savedElements) cam?.play(savedElements.localEl);
        } catch {
            playbackFailed = true;
            autoplayBlocked.value = true;
        }
    }

    async function leave() {
        generation++;
        removePlaybackListeners?.();
        removePlaybackListeners = null;
        playbackFailed = false;
        audioInterrupted = false;
        toggleQueue = Promise.resolve();
        peerUid = null;
        savedCredentials = null;
        savedElements = null;
        joinedUid = null;
        channelName = null;
        mic?.close();
        cam?.close();
        mic = null;
        cam = null;
        remoteUser = null;
        remoteEl = null;
        handlers = {};
        reconnecting.value = false;
        remoteVideoOn.value = false;
        autoplayBlocked.value = false;

        if (!client) return;

        const c = client;
        client = null;
        detachTransport(c);

        try {
            await c.leave();
        } catch {
            // Already disconnected
        }
    }

    onBeforeUnmount(() => {
        leave();
    });

    return {
        micOn,
        micAllowed,
        camOn,
        audioOnly,
        reconnecting,
        remoteVideoOn,
        autoplayBlocked,
        prepareTracks,
        configureMedia,
        join,
        rejoin,
        toggleMic,
        toggleCam,
        switchCamera,
        resumeMedia,
        leave,
    };
}
